import { routes } from '@/config/routes'
import { BENCHMARK_ITEMS, type LeaderboardItem, useBenchmarkApyChart } from '@/hooks/analytics/useBenchmarkApyChart'
import { useRelativeTime } from '@/hooks/analytics/useRelativeTime'
import type { HistoricalBenchmark } from '@/queries/historicalBenchmarks.query'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import type { Vault } from '@/types/vault'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatAmount, formatDate, formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartBrush, ChartBrushOverlay } from '../charts/chartBrush'
import { ChartContainer } from '../charts/chartContainer'
import { ChartWatermark } from '../charts/chartWatermark'
import { Button } from '../common/button'
import {
  DialogBody,
  DialogContentWrapper,
  DialogDescription,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
} from '../common/dialog'
import { Loader } from '../common/loader'
import { ProtocolImage } from '../common/protocolImage'
import { Separator } from '../common/separator'
import { CustomTooltip } from './benchmarkApyComparison'
import { type BenchmarkItem, VaultSelector } from './vaultSelector'

interface LeaderboardProps {
  items: LeaderboardItem[]
}

const Leaderboard = ({ items }: LeaderboardProps) => {
  if (items.length === 0) return null

  return (
    <div className="w-[280px] shrink-0">
      <p className="mb-3 font-semibold text-heading-foreground">Leaderboard</p>
      <ul className="flex flex-col gap-2">
        {items.map((item, i) => (
          <li key={item.key} className="flex items-center gap-2 text-sm">
            <span className="min-w-[18px] text-right text-body-foreground">{i + 1}</span>
            <div className="size-3 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
            {item.vault && <ProtocolImage protocol={item.vault.protocol} className="size-5 shrink-0" />}
            {item.vault ? (
              <Button
                variant="default"
                size="default"
                className="min-w-0 flex-grow items-baseline justify-start gap-1 text-heading-foreground max-xs:flex-wrap"
                asChild
              >
                <Link
                  to={generatePath(routes.opportunity, {
                    vaultId: item.vault.vaultId,
                    network: item.vault.network.name,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  prefetch="intent"
                >
                  <p className="min-w-0 truncate text-sm">{item.name}</p>
                  <span className="shrink-0 first-letter:uppercase">({getNetworkName(item.vault.network.name)})</span>
                </Link>
              </Button>
            ) : (
              <div className="flex min-w-0 flex-1 items-baseline gap-1 text-heading-foreground">
                <p className="min-w-0 truncate text-sm">{item.name}</p>
              </div>
            )}
            <span className="ml-auto shrink-0 text-right font-semibold text-success tabular-nums">
              {item.apy > 0 && '+'}
              {formatDecimalToPercentage(item.apy)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface Props {
  apyRange: VaultApyRange
  chartPeriod: ChartPeriod
  includeRewards: boolean
  ethBenchmarkData?: HistoricalBenchmark
  usdBenchmarkData?: HistoricalBenchmark
  selectedVaults: Vault[]
  onVaultsChange: (vaults: Vault[]) => void
  selectedBenchmarks: BenchmarkItem[]
  onBenchmarksChange: (benchmarks: BenchmarkItem[]) => void
}

export const BenchmarkApyComparisonFullView = ({
  apyRange,
  chartPeriod,
  includeRewards,
  ethBenchmarkData,
  usdBenchmarkData,
  selectedVaults,
  onVaultsChange,
  selectedBenchmarks,
  onBenchmarksChange,
}: Props) => {
  const {
    vaults,
    positionsList,
    isLoading,
    benchmarkApyMap,
    chartData,
    dataKeys,
    nonBenchmarkKeys,
    keyToName,
    getKeyColor,
    getStrokeWidth,
    leaderboardItems,
    lastTimestamp,
    handleSetSelectedVaults,
    handleSetSelectedBenchmarks,
  } = useBenchmarkApyChart({
    apyRange,
    chartPeriod,
    includeRewards,
    ethBenchmarkData,
    usdBenchmarkData,
    selectedVaults,
    selectedBenchmarks,
    onVaultsChange,
    onBenchmarksChange,
  })

  const relativeTime = useRelativeTime(lastTimestamp)
  const canShowBrush = Boolean(dataKeys[0])

  return (
    <DialogBody>
      <DialogHeaderWrapper className="pt-6 md:pt-8">
        <DialogHeader>
          <DialogTitle>Benchmark APY Comparison</DialogTitle>
          <DialogDescription>
            Compare the APY of any vault over time against benchmark rates
            {relativeTime !== null && ` · Updated ${relativeTime}`}
          </DialogDescription>
        </DialogHeader>
        <Separator />
      </DialogHeaderWrapper>
      <DialogScrollArea>
        <DialogContentWrapper className="pt-4 md:pt-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <VaultSelector
              vaults={vaults ?? []}
              selectedVaults={selectedVaults}
              setSelectedVaults={handleSetSelectedVaults}
              benchmarks={BENCHMARK_ITEMS}
              selectedBenchmarks={selectedBenchmarks}
              setSelectedBenchmarks={handleSetSelectedBenchmarks}
              positionsList={positionsList}
              isLoading={!vaults}
              apyRange={apyRange}
              includeRewards={includeRewards}
              benchmarkApyMap={benchmarkApyMap}
            />
          </div>
          <div className="flex flex-wrap items-start gap-x-5 gap-y-2">
            <ChartContainer className="h-80 flex-1 flex-grow basis-60">
              {isLoading ? (
                <Loader />
              ) : (
                <ResponsiveContainer>
                  <AreaChart data={chartData}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
                    <XAxis
                      dataKey="timestamp"
                      tickLine={false}
                      tickMargin={12}
                      type="number"
                      interval="preserveStartEnd"
                      domain={['dataMin', 'dataMax']}
                      tickCount={7}
                      tickFormatter={(timestamp) => formatDate({ timestamp: timestamp * SECOND_IN_MILLISECONDS })}
                    />
                    <YAxis
                      width={52}
                      tickLine={false}
                      tickMargin={8}
                      tickFormatter={(value) =>
                        `${formatAmount(value * 100, { notation: 'compact', maximumFractionDigits: 1 })}%`
                      }
                    />
                    <Tooltip content={<CustomTooltip keyToName={keyToName} />} />
                    {dataKeys.map((key) => {
                      const vaultIndex = nonBenchmarkKeys.indexOf(key)
                      return (
                        <Area
                          key={key}
                          dataKey={key}
                          type="monotone"
                          stroke={getKeyColor(key, vaultIndex)}
                          strokeWidth={getStrokeWidth(key)}
                          fill="transparent"
                          name={key}
                        />
                      )
                    })}
                    <ChartWatermark />
                    {canShowBrush && (
                      <ChartBrush data={chartData}>
                        <AreaChart>
                          <YAxis hide domain={['auto', 'auto']} />
                          {dataKeys.map((key) => {
                            const vaultIndex = nonBenchmarkKeys.indexOf(key)
                            return (
                              <Area
                                key={key}
                                dataKey={key}
                                type="monotone"
                                stroke={getKeyColor(key, vaultIndex)}
                                strokeWidth={getStrokeWidth(key)}
                                fill="transparent"
                                name={key}
                              />
                            )
                          })}
                          <ChartBrushOverlay />
                        </AreaChart>
                      </ChartBrush>
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
            <Leaderboard items={leaderboardItems} />
          </div>
        </DialogContentWrapper>
      </DialogScrollArea>
    </DialogBody>
  )
}
